/* =============================================================================
   AQUORIX — useManifestData.ts  (the data seam)
   Path: aquorix-frontend % src/features/dashboard/pages/useManifestData.ts
   Version 1.4 · 26 August 2026 · Owner: Larry McLean · Authors: Claude + ChatGPT

   This is the ONLY file the executor edits when the page is wired to live data.
   ManifestPage.tsx and ManifestPage.css are the locked visual and are not touched
   during wiring.

   DEMO_MODE = true ships the page on the fixture below, so it renders the moment it
   is dropped in, exactly as the approved prototype. When the manifest read endpoint
   (Viking unit V3) exists, DEMO_MODE flips to false and this hook fetches instead.
   Same pattern as CoursesPage / WalkInsPage.

   VIEW-MODEL -> BACKEND MAPPING (for V3, when wired):
     Trip.scope        <- session type: boat departure vs shore day
     Trip.boat         <- vessel_name (boat) or meet_location (shore)
     Trip.time         <- start_time (operator-local) + session_date
     Trip.guide        <- metadata.lead_guide_name / instructor_name (may be null = TBD)
     Person.first/last <- booking_participants.first_name/last_name · guest_identities.first_name/last_name
     Person.nationality<- nationality_code -> authority_nationality_labels (authority-keyed)
     Person.passport   <- id_document_number
     Person.dob        <- date_of_birth
     Person.gender     <- gender (mapped Male/Female at generation)
     Person.activity   <- derived from booking, with the per-person override (unit V2)
     Person.isMinor / guardian* <- is_minor, guardian_passport_number, guardian_nationality_code
     Person.accommodation <- divers.accommodation (optional, never blocks)
     Person.cert       <- diver certification level (used by the Aqaba/ASEZA mapping only)

   Change log:
   1.0  2026-07-27  Initial. Fixture + typed contract. DEMO_MODE default true.
   1.1  2026-07-28  Live wiring: DEMO_MODE off. useManifestData now fetches
                    GET /api/v1/manifest via apiFetch (same auth pattern as
                    CoursesPage) and maps the V3 response to ManifestData. The
                    fixture is retained behind DEMO_MODE as a fallback.
   1.2  2026-07-29  Add saveIdentity, generateManifestFile, Trip.session_id.
   1.3  2026-08-25  Add backward-compatible profile-aware dive, course, and day
                    workbook generation for internal Ops Board wiring.
   1.4  2026-08-26  Carry booking-snapshot accommodation and room number into
                    the canonical internal Ops Board Manifest preview.
   ============================================================================= */

import { useEffect, useState } from "react";
import { apiFetch } from "../../../utils/api";

export const DEMO_MODE = false;

export type Scope = "boat" | "shore";

export interface Person {
  id: number;
  first: string;
  last: string;
  nationality: string | null;
  passport: string | null;
  dob: string | null;            // ISO yyyy-mm-dd
  gender: "Male" | "Female" | null;
  activity: string;              // one of the 18 CDWS activity labels
  isMinor: boolean;
  guardianPassport: string | null;
  guardianNationality: string | null;
  accommodation: string | null;
  roomNumber: string | null;
  cert: string | null;
}

export interface Trip {
  scope: Scope;
  session_id: number | null;
  boat: string;
  time: string;
  guide: string | null;          // null = TBD
  roster: Person[];
}

export interface ManifestData {
  trips: Trip[];
}

/* The 18-item CDWS activity vocabulary and the nationality list are template truth.
   In the build the nationality list is served from authority_nationality_labels.
   The subset here keeps the fixture playable; the field draws the full 239-list live. */
export const ACTIVITIES: string[] = [
  "Intro Dive","Technical Diving","Safari","Glass Boat","Snorkeling","Towables","Pedalos",
  "Wind Surf","Kite Surf","sailing boats","Submarins","water skis","Training","Free Diving",
  "parachute","Fly Board","Diving","Passenger",
];

export const NATIONALITIES: string[] = [
  "American","Argentine","Australian","Austrian","Belgian","Brazilian","British","Bulgarian",
  "Canadian","Chinese","Croatian","Czech","Danish","Dutch","Egyptian","Emirati","Filipino",
  "Finnish","French","German","Greek","Hungarian","Indian","Irish","Israeli","Italian","Japanese",
  "Jordanian","Korean","Kuwaiti","Lebanese","Malaysian","Mexican","Moroccan","New Zealander",
  "Norwegian","Polish","Portuguese","Romanian","Russian","Saudi","Singaporean","Slovak",
  "South African","Spanish","Swedish","Swiss","Thai","Turkish","Ukrainian",
];

let _pid = 0;
function person(o: Partial<Person>): Person {
  return {
    id: ++_pid, first: "", last: "", nationality: null, passport: null, dob: null, gender: null,
    activity: "Diving", isMinor: false, guardianPassport: null, guardianNationality: null,
    accommodation: null, roomNumber: null, cert: null, ...o,
  };
}

function boatRoster(): Person[] {
  return [
    person({ first: "John", last: "Robinson", nationality: "American", passport: "544210821", dob: "1979-03-14", gender: "Male", activity: "Diving", accommodation: "Nesima Resort", cert: "Advanced Open Water" }),
    person({ first: "Maureen", last: "Robinson", nationality: "American", passport: "544120744", dob: "1981-07-22", gender: "Female", activity: "Diving", accommodation: "Nesima Resort", cert: "Open Water" }),
    person({ first: "Penny", last: "Robinson", nationality: "American", passport: "544330907", dob: "2011-05-02", gender: "Female", activity: "Intro Dive", isMinor: true, guardianPassport: "544210821", guardianNationality: "American", cert: "None" }),
    person({ first: "Hiroshi", last: "Tanaka", nationality: "Japanese", passport: "TK8840392", dob: "1988-11-30", gender: "Male", activity: "Diving", cert: "Rescue Diver" }),
    person({ first: "Lena", last: "Fischer", nationality: "German", passport: null, dob: "1990-02-18", gender: "Female", activity: "Diving", cert: "Divemaster" }),
    person({ first: "Amara", last: "Okeke", nationality: null, passport: "A7710118", dob: "1985-09-09", gender: "Female", activity: "Diving", cert: "Open Water" }),
    person({ first: "Sofia", last: "Marchetti", nationality: "Italian", passport: "YB620556", dob: "1993-12-01", gender: "Female", activity: "Diving", cert: "Advanced Open Water" }),
    person({ first: "Daniel", last: "Cohen", nationality: "Canadian", passport: "GA330240", dob: null, gender: "Male", activity: "Diving", cert: "Open Water" }),
    person({ first: "Yuki", last: "Tanaka", nationality: "Japanese", passport: "TK9010401", dob: "1991-06-15", gender: "Female", activity: "Diving", cert: "Advanced Open Water" }),
    person({ first: "Grace", last: "Robinson", nationality: "American", passport: "544990663", dob: "1955-04-19", gender: "Female", activity: "Snorkeling", accommodation: "Nesima Resort", cert: "None" }),
    person({ first: "Don", last: "West", nationality: "British", passport: "509880288", dob: "1968-08-27", gender: "Male", activity: "Passenger", cert: "None" }),
  ];
}
function shoreRoster(): Person[] {
  return [
    person({ first: "Marco", last: "Bianchi", nationality: "Italian", passport: "YA110233", dob: "1986-01-12", gender: "Male", activity: "Diving", cert: "Advanced Open Water" }),
    person({ first: "Elin", last: "Sundqvist", nationality: "Swedish", passport: null, dob: "1994-10-03", gender: "Female", activity: "Diving", cert: "Open Water" }),
    person({ first: "Omar", last: "Haddad", nationality: "Jordanian", passport: "J5540771", dob: "1990-05-20", gender: "Male", activity: "Free Diving", cert: "Free Diver" }),
    person({ first: "Chloe", last: "Martin", nationality: "French", passport: "18FR4420", dob: "1999-07-08", gender: "Female", activity: "Intro Dive", cert: "None" }),
    person({ first: "Sam", last: "Turner", nationality: "British", passport: "512340199", dob: "1975-02-28", gender: "Male", activity: "Snorkeling", cert: "None" }),
  ];
}

function fixture(): ManifestData {
  _pid = 0;
  return {
    trips: [
      { scope: "boat", session_id: null, boat: "Nesima I", time: "09:00 Departure · 27 July 2026", guide: "Rami Mounir", roster: boatRoster() },
      { scope: "boat", session_id: null, boat: "Nesima II", time: "13:30 Departure · 27 July 2026", guide: null, roster: boatRoster().slice(0, 6) },
      { scope: "shore", session_id: null, boat: "House Reef · Shore", time: "Full day · 27 July 2026 · editable until 17:00", guide: "Yasmin Haddad", roster: shoreRoster() },
    ],
  };
}

/* Map the V3 read-endpoint response (Manifest V3 package, section 4) to the ManifestData
   view-model the page consumes. The shapes are 1:1 by design; this only guards types and
   drops fields the view-model does not use — readiness and the missing list are recomputed
   by the page, and cert is not returned by V3. */
function mapManifest(json: any): ManifestData {
  const trips: Trip[] = Array.isArray(json?.trips)
    ? json.trips.map((t: any): Trip => ({
        scope: t?.scope === "shore" ? "shore" : "boat",
        session_id: typeof t?.session_id === "number" ? t.session_id : null,
        boat: typeof t?.boat === "string" ? t.boat : "",
        time: typeof t?.time === "string" ? t.time : "",
        guide: t?.guide == null ? null : String(t.guide),
        roster: Array.isArray(t?.roster)
          ? t.roster.map((p: any): Person => ({
              id: typeof p?.participant_id === "number" ? p.participant_id : ++_pid,
              first: p?.first ?? "",
              last: p?.last ?? "",
              nationality: p?.nationality ?? null,
              passport: p?.passport ?? null,
              dob: p?.dob ?? null,
              gender: p?.gender === "Male" || p?.gender === "Female" ? p.gender : null,
              activity: p?.activity ?? "Diving",
              isMinor: !!p?.isMinor,
              guardianPassport: p?.guardianPassport ?? null,
              guardianNationality: p?.guardianNationality ?? null,
              accommodation: p?.accommodation ?? null,
              roomNumber: p?.roomNumber ?? null,
              cert: null,
            }))
          : [],
      }))
    : [];
  return { trips };
}

/**
 * Returns the manifest data for the page.
 * DEMO_MODE true:  returns the fixture above (synchronous).
 * DEMO_MODE false: fetches GET /api/v1/manifest?date=... via apiFetch and maps the V3
 *   response to ManifestData. Starts empty, then updates when the fetch resolves; the
 *   page reflects this (Law 16) and re-syncs its editable roster when it changes.
 *   No date passed = the endpoint defaults to today in the operator timezone.
 */
export function useManifestData(date?: string): ManifestData {
  const [data, setData] = useState<ManifestData>(() => (DEMO_MODE ? fixture() : { trips: [] }));

  useEffect(() => {
    if (DEMO_MODE) return;
    let cancelled = false;
    (async () => {
      try {
        const qs = date ? `?date=${encodeURIComponent(date)}` : "";
        const res = await apiFetch(`/api/v1/manifest${qs}`);
        if (!res.ok) throw new Error(`manifest HTTP ${res.status}`);
        const json = await res.json();
        if (!cancelled) setData(mapManifest(json));
      } catch (err) {
        console.error("Manifest load failed", err);
        if (!cancelled) setData({ trips: [] });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [date]);

  return data;
}


/* ---- identity write (AQX-097) ---- one field per call, keyed by participant_id ---- */
export async function saveIdentity(
  participantId: number,
  field: string,
  value: string | null,
  profileCode: ManifestProfileCode = "CDWS_EG"
): Promise<void> {
  const res = await apiFetch(
    `/api/v1/manifest/participant/${participantId}/identity`
      + `?profile_code=${encodeURIComponent(profileCode)}`,
    { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ field, value }) }
  );
  if (!res.ok) {
    let msg = "Save failed";
    try { const j = await res.json(); if (j && j.error) msg = j.error; } catch (_e) { /* keep default */ }
    throw new Error(msg);
  }
}

/* ---- Manifest workbook generation ---- */

export type ManifestProfileCode =
  | "CDWS_EG"
  | "ASEZA_JO_PROVISIONAL"
  | "AQX_STOCK_V1";

export type ManifestFileScope =
  | { sessionId: number }
  | { courseRunId: number }
  | { date: string };

export type GeneratedManifestFile = {
  blob: Blob;
  filename: string;
};

function manifestFilenameFromResponse(
  response: Response,
  fallback: string
): string {
  const disposition =
    response.headers.get("Content-Disposition") || "";

  const utf8Match = disposition.match(
    /filename\*=UTF-8''([^;]+)/i
  );

  if (utf8Match && utf8Match[1]) {
    try {
      return decodeURIComponent(
        utf8Match[1].replace(/^["']|["']$/g, "")
      );
    } catch (_error) {
      return utf8Match[1].replace(/^["']|["']$/g, "");
    }
  }

  const filenameMatch = disposition.match(
    /filename="?([^";]+)"?/i
  );

  return filenameMatch && filenameMatch[1]
    ? filenameMatch[1]
    : fallback;
}

export async function generateScopedManifestFile(
  scope: ManifestFileScope,
  profileCode: ManifestProfileCode
): Promise<GeneratedManifestFile> {
  const params = new URLSearchParams();

  if ("sessionId" in scope) {
    params.set("session_id", String(scope.sessionId));
  } else if ("courseRunId" in scope) {
    params.set("course_run_id", String(scope.courseRunId));
  } else {
    params.set("date", scope.date);
  }

  params.set("profile_code", profileCode);

  const response = await apiFetch(
    `/api/v1/manifest/file?${params.toString()}`
  );

  if (!response.ok) {
    let message = "Manifest file generation failed";

    try {
      const body = await response.json();

      if (body && typeof body.error === "string") {
        message = body.error;
      } else if (
        body
        && typeof body.message === "string"
      ) {
        message = body.message;
      }
    } catch (_error) {
      // Preserve the deterministic fallback message.
    }

    throw new Error(message);
  }

  const fallback =
    profileCode === "CDWS_EG"
      ? "GuestList.xlsx"
      : `Manifest-${profileCode}.xlsx`;

  return {
    blob: await response.blob(),
    filename: manifestFilenameFromResponse(
      response,
      fallback
    )
  };
}

/*
 * Backward-compatible legacy CDWS dive-session helper.
 * Existing ManifestPage callers remain unchanged.
 */
export async function generateManifestFile(
  sessionId: number
): Promise<Blob> {
  const generated = await generateScopedManifestFile(
    { sessionId },
    "CDWS_EG"
  );

  return generated.blob;
}
